import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Stack } from "@/core/components/ui/stack";
import {
  DocumentReviewActions,
  DocumentReviewPreview,
} from "@/accounts-payable/common";
import { useGetReceipt } from "@/accounts-payable/query";
import {
  AP_TAB,
  AP_TAB_LABEL,
  apListPath,
  parseApTab,
} from "@/accounts-payable/utils";
import { useBusiness } from "@/core/business";
import { ReceiptReviewHeader } from "./receipt-review-header";

/**
 * Full-page review of one receipt: the document beside the agent's proposals,
 * approved or rejected in place — the receipt counterpart of the invoice page.
 */
const AccountsPayableReceiptPage = () => {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const [searchParams] = useSearchParams();
  const fromTab = parseApTab(searchParams.get("tab")) ?? AP_TAB.receipts;

  const { data: receipt, isLoading } = useGetReceipt(businessId, receiptId);

  const back = (
    <button
      type="button"
      onClick={() => navigate(apListPath(fromTab))}
      className="flex w-fit items-center gap-1.5 text-sm text-tertiary-text transition-colors hover:text-primary-text"
    >
      <ArrowLeftIcon className="h-3.5 w-3.5" />
      Back to {AP_TAB_LABEL[fromTab]}
    </button>
  );

  if (!receipt) {
    return (
      <Stack gap="md" className="mx-auto max-w-[1680px] px-8 py-6">
        {back}
        <span className="text-tertiary-text">
          {isLoading ? "Loading receipt…" : "Receipt not found."}
        </span>
      </Stack>
    );
  }

  return (
    <Stack fullWidth className="mx-auto max-w-[1680px] px-8 pb-12">
      <Stack gap="md" fullWidth className="pt-4">
        {back}
        <ReceiptReviewHeader receipt={receipt} businessId={businessId} />
      </Stack>

      <Stack
        gap="none"
        fullWidth
        className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_480px]"
      >
        <DocumentReviewPreview document={receipt} />
        <DocumentReviewActions
          documentId={receipt.id}
          documentType="receipt"
          documentHasJob={!!receipt.st_job_external_id}
          businessId={businessId}
        />
      </Stack>
    </Stack>
  );
};

export default AccountsPayableReceiptPage;
